import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import styles from './LoginPage.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearAllAuthData, ensureCsrfToken, saveToken } from '../utils/auth';
import { isIOSPWA, setupTokenPersistence } from '../utils/pwaUtils';

// 개발 환경 체크 헬퍼
const isDev = () => process.env.NODE_ENV === 'development';

// 다국어 텍스트
const TEXT = {
  cacheCleared: {
    ko: '캐시가 성공적으로 지워졌습니다.',
    en: 'Cache has been cleared successfully.',
    fr: 'Le cache a été effacé avec succès.'
  },
  cacheClearError: {
    ko: '캐시 지우기 중 오류가 발생했습니다.',
    en: 'An error occurred while clearing the cache.',
    fr: 'Une erreur s\'est produite lors de l\'effacement du cache.'
  },
  internalError: {
    ko: '내부 오류: setUserInfo가 함수가 아닙니다.',
    en: 'Internal error: setUserInfo is not a function.',
    fr: 'Erreur interne: setUserInfo n\'est pas une fonction.'
  },
  loginFailed: {
    ko: '로그인에 실패했습니다.',
    en: 'Login failed.',
    fr: 'Échec de la connexion.'
  },
  serverError: {
    ko: '서버 오류가 발생했습니다.',
    en: 'Server error occurred.',
    fr: 'Une erreur serveur s\'est produite.'
  },
  loggingIn: {
    ko: '로그인 중...',
    en: 'Logging in...',
    fr: 'Connexion en cours...'
  },
  login: {
    ko: '로그인',
    en: 'Login',
    fr: 'Connexion'
  },
  rememberMe: {
    ko: '자동로그인유지',
    en: 'Keep me signed in',
    fr: 'Rester connecte'
  }
};

// 언어 감지 함수
function getLocale() {
  const lang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (lang.startsWith('fr')) return 'fr';
  if (lang.startsWith('ko')) return 'ko';
  if (lang.startsWith('en')) return 'en';
  return 'ko'; // 기본값을 한국어로 설정
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locale = getLocale();

  // URL에서 리다이렉트 파라미터 읽기
  const searchParams = new URLSearchParams(location.search);
  const rawRedirectUrl = searchParams.get('redirect');

  // 안전한 리다이렉트 URL 검증 (Open Redirect 방지)
  const getSafeRedirectUrl = (url) => {
    if (!url) return null;

    // 상대 경로만 허용 (/ 로 시작하고 // 로 시작하지 않음)
    if (url.startsWith('/') && !url.startsWith('//')) {
      // 프로토콜 우회 시도 방지 (javascript:, data: 등)
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.origin === window.location.origin) {
          return url;
        }
      } catch {
        return null;
      }
    }

    return null; // 외부 URL은 차단
  };

  const redirectUrl = getSafeRedirectUrl(rawRedirectUrl);

  const { setUserInfo } = useContext(UserContext);
  const { userInfo } = useContext(UserContext);

  // LoginForm 상태/로직
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  // 이미 로그인된 경우 사용자 유형에 따라 리다이렉트
  useEffect(() => {
    if (userInfo && userInfo.level) {
      // 게스트 아티스트는 /guest로, 그 외는 /client로
      const redirectPath = userInfo.level === 'guest_artist' ? '/guest' : '/client';
      navigate(redirectPath, { replace: true });
    }
  }, [userInfo, navigate]);

  // 로그인 성공 시 콜백 (최적화)
  const handleLogin = (user) => {
    if (typeof setUserInfo !== 'function') {
      alert(TEXT.internalError[locale]);
      return;
    }

    if (isDev()) console.log('사용자 정보 설정 중...');
    setUserInfo(user);

    setLoading(false);

    if (redirectUrl) {
      if (isDev()) console.log('리다이렉트 URL로 이동:', redirectUrl);
      window.location.href = redirectUrl;
    } else {
      // 게스트 아티스트는 /guest로, 그 외는 /client로
      const defaultPath = user.level === 'guest_artist' ? '/guest' : '/client';
      if (isDev()) console.log('기본 페이지로 이동:', defaultPath);
      window.location.href = defaultPath;
    }
  };

  // 새로고침 핸들러
  const handleRefresh = () => {
    window.location.reload();
  };

  // 캐시 지우기 핸들러
  const handleClearCache = async () => {
    try {
      // 1. 브라우저 캐시 지우기
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        if (isDev()) console.log('브라우저 캐시 지워짐');
      }

      clearAllAuthData();
      localStorage.removeItem('csrfToken');
      if (isDev()) console.log('인증 저장소 정리됨');

      alert(TEXT.cacheCleared[locale]);

      // 5. 페이지 새로고침
      window.location.reload();
    } catch (error) {
      console.error('캐시 지우기 오류:', error);
      alert(TEXT.cacheClearError[locale]);
    }
  };

  // PWA 키보드 문제 해결을 위한 단순화된 useEffect
  useEffect(() => {
    let isKeyboardOpen = false;

    // iOS PWA 감지 - 외부 함수와 중복이지만 useEffect 내에서 필요
    // eslint-disable-next-line no-unused-vars
    const isIOSPWA = () => {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        window.navigator.standalone === true;
    };

    // 입력 필드 포커스 처리 (단순화)
    const handleInputFocus = (e) => {
      if (e.target.tagName === 'INPUT') {
        console.log('입력 필드 포커스됨');

        // 뷰포트 조정 (한 번만)
        if (!isKeyboardOpen) {
          isKeyboardOpen = true;
          const viewport = document.querySelector('meta[name=viewport]');
          if (viewport) {
            viewport.setAttribute('content', 'width=device-width, initial-scale=1, user-scalable=no, viewport-fit=cover');
          }
        }

        // 입력 필드가 화면 중앙에 오도록 조정
        setTimeout(() => {
          e.target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 100);
      }
    };

    // 입력 필드 블러 처리 (키보드 닫힘)
    const handleInputBlur = (e) => {
      if (e.target.tagName === 'INPUT') {
        console.log('입력 필드 블러됨');

        // 키보드가 닫힐 것으로 예상하여 뷰포트 복원
        setTimeout(() => {
          isKeyboardOpen = false;
          const viewport = document.querySelector('meta[name=viewport]');
          if (viewport) {
            viewport.setAttribute('content', 'width=device-width, initial-scale=1, user-scalable=no, viewport-fit=cover');
          }
        }, 300);
      }
    };

    // 이벤트 리스너 추가 (단순화)
    document.addEventListener('focusin', handleInputFocus);
    document.addEventListener('focusout', handleInputBlur);

    return () => {
      document.removeEventListener('focusin', handleInputFocus);
      document.removeEventListener('focusout', handleInputBlur);
    };
  }, []);

  // 폼 제출 핸들러 (최적화)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isDev()) console.log('로그인 시작...');
      await ensureCsrfToken({ forceRefresh: true });

      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_login_id: loginId,
          password,
          remember_me: rememberMe,
        }),
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok) {
        if (isDev()) console.log('로그인 성공, 토큰 저장 중...');

        saveToken(data.token || null);

        let userData = data.user || data;

        if (!userData || !userData.user_id) {
          if (isDev()) console.log('사용자 정보 별도 요청...');
          const meRes = await fetch('/api/me', {
            method: 'GET',
            credentials: 'include'
          });
          const meData = await meRes.json();
          userData = meData.user || meData;
        }

        if (isDev()) console.log('로그인 완료, 페이지 이동 중...');

        if (isIOSPWA() && data.token) {
          setTimeout(() => {
            if (isDev()) console.log('iOS PWA 토큰 지속성 설정');
            setupTokenPersistence(data.token);
          }, 100);
        }

        if (typeof handleLogin === 'function') {
          handleLogin(userData);
        }
      } else {
        console.error('로그인 실패:', data.message || data.error);
        alert(data.message || data.error || TEXT.loginFailed[locale]);
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      alert(TEXT.serverError[locale]);
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>BACKSTREET</div>
      <div className={styles.inputRow}>
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
          type="button"
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </button>

        <form id="loginForm" onSubmit={handleSubmit} className={styles.form}>
          <input
            type="text"
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            className={styles.input}
            placeholder="ID"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={styles.input}
            placeholder="PASSWORD"
            autoComplete="current-password"
            required
          />
          <label className={styles.rememberMeLabel}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className={styles.rememberMeCheckbox}
            />
            <span>{TEXT.rememberMe[locale]}</span>
          </label>
        </form>

        <button
          type="submit"
          form="loginForm"
          className={styles.submitButton}
          disabled={loading}
          aria-label={loading ? TEXT.loggingIn[locale] : TEXT.login[locale]}
        >
          {loading ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          )}
        </button>
      </div>

      {/* System Buttons */}
      <div className={styles.systemButtons}>
        <button
          onClick={handleRefresh}
          className={styles.sysButton}
          type="button"
          title="Refresh"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>

        <button
          onClick={handleClearCache}
          className={styles.sysButton}
          type="button"
          title="Clear Cache"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      </div>
    </div>
  );
}
